export class GlobalFn {

    // // ---------------------------------------------------------------------------
    // // Convenience wrappers (optional — import whichever fits your call sites)
    // // ---------------------------------------------------------------------------

    // /** Rotate 90° clockwise */
    // static rotateCW = (blob: Blob, quality?: number): Promise<Blob> => GlobalFn.rotateImageBlob(blob, "cw", "image/jpeg", quality);

    // /** Rotate 90° counter-clockwise */
    // static rotateCCW = (blob: Blob, quality?: number): Promise<Blob> => GlobalFn.rotateImageBlob(blob, "ccw", "image/jpeg", quality);

    /**
     * rotateImageBlob
     *
     * Rotates a JPEG (or any image/* Blob) 90° clockwise or counter-clockwise
     * entirely client-side using an OffscreenCanvas (falls back to a regular
     * Canvas element in environments that don't support OffscreenCanvas).
     *
     * @param blob      - Source image Blob (JPEG, PNG, WebP …)
     * @param direction - "cw" = 90° clockwise | "ccw" = 90° counter-clockwise
     * @param mimeType  - Output MIME type (default: "image/jpeg")
     * @param quality   - Encoder quality 0–1 for lossy formats (default: 0.92)
     * @returns         Promise<Blob> — rotated image as a new Blob
     */
    static async rotateImageBlob(
        blob: Blob,
        direction: "cw" | "ccw",
        mimeType = "image/jpeg",
        quality = 0.92,
    ): Promise<Blob> {
        // 1. Decode the blob into an ImageBitmap (no DOM img element needed)
        const bitmap = await createImageBitmap(blob);
        const { width: w, height: h } = bitmap;

        // After a 90° rotation the axes swap: output width = source height, etc.
        const outW = h;
        const outH = w;

        // 2. Create a canvas — prefer OffscreenCanvas for worker compatibility
        let ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
        let canvas: OffscreenCanvas | HTMLCanvasElement;

        if (typeof OffscreenCanvas !== "undefined") {
            canvas = new OffscreenCanvas(outW, outH);
            ctx = canvas.getContext("2d") as OffscreenCanvasRenderingContext2D;
        } else {
            canvas = document.createElement("canvas");
            canvas.width = outW;
            canvas.height = outH;
            ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
        }

        // 3. Apply the rotation transform
        //    CW  (+90°): translate to (outW, 0)     then rotate  π/2
        //    CCW (-90°): translate to (0, outH)     then rotate -π/2
        ctx.translate(direction === "cw" ? outW : 0, direction === "cw" ? 0 : outH);
        ctx.rotate(direction === "cw" ? Math.PI / 2 : -Math.PI / 2);

        // 4. Draw — origin is now at the rotation pivot, source starts at (0,0)
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close(); // free GPU memory

        // 5. Encode back to a Blob
        if (canvas instanceof OffscreenCanvas) {
            return canvas.convertToBlob({ type: mimeType, quality });
        }

        // HTMLCanvasElement fallback — wrap toBlob callback in a Promise
        return new Promise<Blob>((resolve, reject) => {
            (canvas as HTMLCanvasElement).toBlob(
                (result) => (result ? resolve(result) : reject(new Error("Canvas toBlob returned null"))),
                mimeType,
                quality,
            );
        });
    }
}
